import { Controller } from "@hotwired/stimulus"

// Server-driven dependent dropdown (mobile task form: type → purpose). When
// a source select changes, point the target turbo frame at `url` with the
// select's param appended; the server re-renders JUST the dependent field
// inside the frame. The relationship (task type → purposes) stays entirely
// server-side — no client-side copy of the mapping, matching the documented
// "light UX hinting + authoritative server validation" pattern while making
// the dependent field actually selectable without a full page reload
// (desktop re-scopes it live in React; this is the Hotwire equivalent).
//
// Failed reload (CONEK-8371). When the frame's request never gets a response
// (offline, dropped connection), Turbo only logs it and leaves the frame as
// it was: the dependent field stays disabled — or keeps the options of the
// previous choice — with nothing on screen saying why. An optional `error`
// target is revealed in that case and hidden again on the next reload or
// successful load; `retry` re-requests the frame's current URL. The host
// renders the error target (its copy is host i18n), so without one the
// controller behaves exactly as before. Put the error target OUTSIDE the
// frame, as a sibling inside the controller element: a frame load replaces
// everything inside the frame, so an error target placed there would vanish
// with the first successful response and never come back. Aborted requests (a newer choice
// superseding an in-flight one) are not errors: Turbo does not report them.
// A `ui--dependent-frame:error` event is dispatched for hosts that want more.
//
// Usage:
//   <div data-controller="ui--dependent-frame"
//        data-ui--dependent-frame-url-value="/mobile/tasks/new"
//        data-ui--dependent-frame-param-value="task[task_type_id]">
//     <div data-action="change->ui--dependent-frame#reload">…type select…</div>
//     <turbo-frame id="task_purpose_field"
//                  data-ui--dependent-frame-target="frame">…purpose select…</turbo-frame>
//     <%# error target: outside the frame, never inside it %>
//     <div data-ui--dependent-frame-target="error" role="alert" hidden>
//       …message… <button type="button" data-action="ui--dependent-frame#retry">Retry</button>
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["frame", "error"]
  static values = { url: String, param: String }

  initialize() {
    this.onFrameError = this.onFrameError.bind(this)
    this.onFrameLoad = this.onFrameLoad.bind(this)
  }

  frameTargetConnected(frame) {
    frame.addEventListener("turbo:fetch-request-error", this.onFrameError)
    frame.addEventListener("turbo:frame-load", this.onFrameLoad)
  }

  frameTargetDisconnected(frame) {
    frame.removeEventListener("turbo:fetch-request-error", this.onFrameError)
    frame.removeEventListener("turbo:frame-load", this.onFrameLoad)
  }

  reload(event) {
    const value = event.target?.value
    const url = new URL(this.urlValue, window.location.origin)
    if (value) {
      url.searchParams.set(this.paramValue, value)
    } else {
      url.searchParams.delete(this.paramValue)
    }
    this.hideError()
    // Setting src makes the frame fetch with the Turbo-Frame header; Turbo
    // extracts the matching frame from the full response and swaps it in.
    this.frameTarget.setAttribute("src", url.toString())
  }

  retry() {
    if (!this.frameTarget.getAttribute("src")) return
    this.hideError()
    this.frameTarget.reload()
  }

  // Only the frame's own request: a nested frame bubbling its error up is
  // not this field failing.
  onFrameError(event) {
    if (event.target !== this.frameTarget) return
    if (this.hasErrorTarget) this.errorTarget.hidden = false
    this.dispatch("error", { detail: { error: event.detail?.error } })
  }

  onFrameLoad(event) {
    if (event.target !== this.frameTarget) return
    this.hideError()
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }
}
